<template>
  <div id="lm-list-2" class="wrapper-content w-100 position-relative">
    <LmComponentLoading v-if="isContentPageLoading" :zIndex="{'z-index': 1008}" />
    <h2>{{titleList}}</h2>
    <LmBreadcrumb v-if="showLmBreadcrumb" />
    <h4
      v-for="(subTitle, index) in subTitlesList"
      :key="index"
    >
      {{ subTitle }}
    </h4>
    <div class="row">
      <div class="col-md-7 mb-3">
        <LmSearcher
          ref="lmSearcher"
          :lmSuperPowerSelects="searcher.lmSuperPowerSelects"
          :searchOnFieldsToSelect="searcher.searchOnFieldsToSelect"
          :searchOnFieldsSelectedProp="searcher.searchOnFieldsSelected"
          :termProp="searcher.term"
          :dateFromProp="searcher.dateFrom"
          :dateUpToProp="searcher.dateUpTo"
          :maxDateUpTo="searcher.maxDateUpTo"
          :showFilterTime="searcher.showFilterTime"
        />
      </div>
      <div class="col-md-5 mt-sm-0 col-12 d-flex justify-content-md-end mb-3" v-if="showFilters && tableItemsTotal">
        <LmFilter
          ref="lmFilter"
          :aggregates="filter.aggregatesToSelect"
          :aggregatesSelectedProp="filter.aggregatesSelected"
          :totalResult="filter.totalResult"
          :loadingProp="isDataProcessing"
        />
      </div>
    </div>
    <div class="position-relative">
      <LmComponentLoading v-if="isDataProcessing" />
      <LmError v-if="tableItemsTotal === 0" />
      <div class="row" v-if="showMap && tableItemsTotal > 0">
        <div class="col-12">
          <LmMap :markers="table.items" :map="map" />
        </div>
      </div>
      <div class="row" v-if="showTable && tableItemsTotal > 0">
        <div class="col-12" v-if="!isTableProcessing">
          <b-table
            v-bind="table"
            :striped="tableStriped"
            :responsive="tableResponsive"
            :hover="tableHover"
            :sort-by="orderBy"
            :sort-desc="tableSortDesc"
            no-local-sorting
            @sort-changed="tableSortingChanged"
            :class="{'lm-table-while-loading': isDataProcessing}"
            >
            <template v-slot:cell()="row">
              <template v-if="row.value.type === 'badge'">
                <b-badge
                  v-for="(tag, index) in row.value.arguments.value.split(',')"
                  :key="index"
                  :variant="variantBuilder(row.value.arguments.variant, index)"
                  pill
                  class="mr-1"
                  >{{ tag }}</b-badge>
              </template>
              <template v-else-if="row.value.type === 'image'">
                <img
                :src="row.value.arguments.url"
                :alt="row.value.arguments.alt"
                :style="{'max-width': row.value.arguments.maxWidth || '100%'}"
                >
              </template>
              <template v-else-if="row.value.type === 'progress'">
                <b-progress class="mt-1" :max="100">
                  <b-progress-bar :value="row.value.arguments.value" :variant="row.value.arguments.variant" />
                </b-progress>
              </template>
                <template v-else-if="row.value.type === 'internalLink'">
                  <router-link v-if="row.field.key in row.item" :to="row.value.arguments.to">{{row.value.arguments.text}}</router-link>
                </template>
                <template v-else-if="row.value.type === 'externalLink'">
                  <a v-if="row.field.key in row.item" :href="row.value.arguments.href">{{row.value.arguments.text}}</a>
                </template>
              <template v-else-if="row.value.type === 'action'">
                <template v-for="(action, index) in row.value.actions">
                  <router-link
                    :key="index"
                    v-if="action.type === 'path'"
                    :to="action.arguments.to"
                    class="mr-3 text-primary"
                    >
                    <font-awesome-layers>
                      <font-awesome-icon
                        :icon="action.arguments.fontAwesomeIcon || 'edit'"
                        size="lg"
                        class="clickable"
                        v-b-tooltip.hover.top="action.arguments.tooltip"
                        />
                      <font-awesome-layers-text
                        v-if="action.arguments.showDot"
                        class="text-white fa-layers-counter"
                        transform="down-4 right-8 shrink-10"
                        value=""
                        />
                    </font-awesome-layers>
                  </router-link>
                  <font-awesome-icon
                    :key="index"
                    id="open-email-preview-modal"
                    v-if="action.type === 'openEmailPreviewModal'"
                    :icon="action.arguments.fontAwesomeIcon || 'search'"
                    size="lg"
                    class="clickable text-primary mr-2"
                    v-b-tooltip.hover.top="action.arguments.tooltip"
                    @click="openEmailPreviewModal(action.arguments.id)"
                  />
                  <font-awesome-icon
                    :key="index"
                    id="open-delete-modal"
                    v-if="action.type === 'openDeleteModal'"
                    :icon="action.arguments.fontAwesomeIcon || 'trash'"
                    size="lg"
                    class="clickable text-primary mr-2"
                    @click="openDeleteModal(action.arguments.id, action.arguments.endPointPath, action.arguments.message, action.arguments.successMessage)"
                    v-b-tooltip.hover.top="action.arguments.tooltip"
                  />
                </template>
              </template>
              <template v-else>
                {{row.value}}
              </template>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row" v-if="tableItemsTotal > 0">
        <div v-if="table.items.length" class="showed-items col-md-2 mt-3 col-12 align-self-center">
          <p>
            {{ $route.query.page ? (itemsPerPage * $route.query.page) + 1 - itemsPerPage : 1 }}
            {{$t('to')}}
            {{ getToOfPagination }}
            {{$t('of')}}
            {{tableItemsTotal}} </p>
        </div>
        <div class="col-md-7 mt-sm-3 col-12">
          <b-pagination-nav
            v-bind="pagination"
            :link-gen="linkGen"
            :limit="pagination.limit || 6"
            :value="$route.query.page || 1"
            :number-of-pages="tableItemsTotal ? Math.ceil(tableItemsTotal / itemsPerPage) : 1"
            :hide-ellipsis="paginationHideEllipsis"
            :first-text="paginationFirstText"
            :last-text="paginationLastText"
            :pills="pagination.pills || false"
            use-router
          />
        </div>
      </div>
      <div class="row" v-if="Object.keys(buttons).length">
        <div class="col-12">
          <LmButton
            v-if="'filterCreator' in buttons && buttons.filterCreator.show"
            type="button"
            lmButtonRootWrapperClass="d-inline-block mr-1"
            lmButtonWrapperClass="col"
            buttonClass="btn-danger"
            :value="$t(buttons.filterCreator.value)"
            :isProcessing="buttons.filterCreator.isProcessing"
            @click.native="createFilterMethod"
          />
          <LmButton
            v-if="'segment' in buttons && buttons.segment.show"
            type="button"
            lmButtonRootWrapperClass="d-inline-block mr-1"
            lmButtonWrapperClass="col"
            buttonClass="btn-danger"
            :value="$t(buttons.segment.value)"
            :isProcessing="buttons.segment.isProcessing"
            @click.native="createSegmentMethod"
          />
        </div>
      </div>
    </div>
    <LmTemplatePreviewModal ref="lmTemplatePreviewModal" />
    <LmConfirmationModal ref="lmConfirmationModal" title="Aviso" />
    <LmCreateSegmentModal
      ref="lmCreateFilterModal"
      v-if="'filterCreator' in buttons"
      :title="buttons.filterCreator.modalTitle"
      :afterCreateSegmentRedirectPath="buttons.filterCreator.afterCreateSegmentRedirectPath"
      :fullFrontEndUrl="buttons.filterCreator.fullFrontEndUrl"
      :apiEndpoint="this.endpointUri.path + this.endpointUri.queryString"
      :pathUrlToSendData="buttons.filterCreator.pathUrlToSendData"
      />
    <LmCreateSegmentModal
      ref="lmCreateSegmentModal"
      v-if="'segment' in buttons"
      :title="buttons.segment.modalTitle"
      :afterCreateSegmentRedirectPath="buttons.segment.afterCreateSegmentRedirectPath"
      :fullFrontEndUrl="buttons.segment.fullFrontEndUrl"
      :apiEndpoint="this.endpointUri.path + this.endpointUri.queryString"
      :pathUrlToSendData="buttons.segment.pathUrlToSendData"
      :hash="buttons.segment.hash"
      />
  </div>
</template>

<script>
import commonsApi from '@/api/commons'

import LmTemplatePreviewModal from '@/components/modals/LmTemplatePreviewModal.vue'
import LmConfirmationModal from '@/components/modals/LmConfirmationModal.vue'
import LmCreateSegmentModal from '@/components/modals/LmCreateSegmentModal.vue'
import LmSearcher from '@/components/LmSearcher.vue'
import LmFilter from '@/components/LmFilter.vue'
import LmMap from '@/components/LmMap.vue'
import LmButton from '@/components/form/elements/LmButton.vue'
import lmToasts from '@/mixins/lmToasts.js'
import LmError from '@/components/LmError.vue'

import urlQueryBuilder from '@/mixins/urlQueryBuilder.js'

export default {
  mixins: [urlQueryBuilder, lmToasts],
  components: {
    LmTemplatePreviewModal,
    LmConfirmationModal,
    LmCreateSegmentModal,
    LmSearcher,
    LmFilter,
    LmMap,
    LmButton,
    LmError
  },
  data () {
    return {
      endpointUri: {
        path: '',
        queryString: '',
        queries: {
          offset: 0,
          limit: 10,
          term: null,
          searchOnFields: null,
          dateFrom: null,
          dateUpTo: null,
          aggregates: [],
          orderBy: null,
          order: 'desc'
        }
      },
      showLmBreadcrumb: false,
      isContentPageLoading: true,
      isTableProcessing: true,
      isDataProcessing: true,
      titleList: '',
      subTitlesList: [],
      offset: 0,
      tableItemsTotal: 0,
      orderBy: null,
      order: 'desc',
      itemsPerPage: 10,
      showTable: true,
      showMap: false,
      map: {
        LMap: {
          props: {}
        },
        LPopup: {
          props: {}
        }
      },
      table: {
        fields: [],
        items: []
      },
      filter: {
        aggregatesToSelect: {},
        aggregatesSelected: [],
        totalResult: 0
      },
      pagination: {},
      searcher: {
        lmSuperPowerSelects: [],
        searchOnFieldsToSelect: [],
        searchOnFieldsSelected: [],
        term: null,
        dateFrom: '',
        dateUpTo: '',
        maxDateUpTo: 'today',
        showFilterTime: false
      },
      buttons: {}
    }
  },
  props: {
    listEndpointUri: {
      type: String,
      default: '',
      required: true
    }
  },
  computed: {
    showFilters () {
      return JSON.stringify(this.filter.aggregatesToSelect) !== '{}' || this.filter?.aggregatesSelected?.length
    },
    getToOfPagination () {
      if (this.$route.query.page && (this.itemsPerPage * this.$route.query.page) <= this.tableItemsTotal) {
        return this.itemsPerPage * this.$route.query.page
      } else if (this.$route.query.page && (this.itemsPerPage * this.$route.query.page) > this.tableItemsTotal) {
        return this.tableItemsTotal
      } else if (this.tableItemsTotal < this.itemsPerPage) {
        return this.tableItemsTotal
      } else {
        return this.itemsPerPage
      }
    },
    tableSortDesc () {
      return this.order === 'desc'
    },
    tableStriped () {
      return this.table.striped !== false
    },
    tableResponsive () {
      return this.table.responsive !== false
    },
    tableHover () {
      return this.table.hover !== false
    },
    paginationHideEllipsis () {
      return this.pagination.hideEllipsis !== false
    },
    paginationFirstText () {
      return this.pagination.firstText || this.$t('first')
    },
    paginationLastText () {
      return this.pagination.lastText || ''
    }
  },
  methods: {
    createFilterMethod () {
      this.buttons.filterCreator.fullFrontEndUrl = window.location.href
      this.$refs.lmCreateFilterModal.lmShow()
    },
    createSegmentMethod () {
      this.buttons.segment.fullFrontEndUrl = window.location.href
      this.$refs.lmCreateSegmentModal.lmShow()
    },
    tableSortingChanged (event) {
      this.urlQueryBuilder({ orderBy: event.sortBy })
      this.urlQueryBuilder({ order: event.sortDesc === true ? 'desc' : 'asc' })
    },
    openEmailPreviewModal (idEmailTemplate) {
      this.$refs.lmTemplatePreviewModal.openEmailPreviewModal(idEmailTemplate)
    },
    variantBuilder (row, index) {
      return row?.split(',')[index] || 'info'
    },
    async openDeleteModal (id, endPointPath, message, successMessage) {
      this.$refs.lmConfirmationModal.setMessageProps(message || 'Está seguro que desea eliminar este registro?')
      try {
        await this.$refs.lmConfirmationModal.lmShow()
        this.isDataProcessing = true
        await commonsApi.axiosDeleteWrapper(process.env.VUE_APP_API_URL + endPointPath + id)
        this.setDataPageAndComponentsProps()
        if (successMessage) {
          this.lmToastSuccessMessage(successMessage, 'Registro Eliminado')
        }
      } catch (error) {
        const jsonError = JSON.stringify(error)
        const parseError = JSON.parse(jsonError)
        const errorMessage = parseError.response?.data?.message
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          toaster: 'b-toaster-bottom-center',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isDataProcessing = false
      }
    },
    linkGen (pageNum) {
      return {
        query: {
          ...this.$route.query,
          page: pageNum
        }
      }
    },
    queryApiUrlBuilder () {
      this.endpointUri = {
        path: this.listEndpointUri,
        queries: {
          offset: this.$route.query.page && this.$route.query.page > 1 ? (this.$route.query.page * this.itemsPerPage) - this.itemsPerPage : 0,
          limit: this.itemsPerPage,
          term: this.$route.query.term || null,
          searchOnFields: (() => {
            if (this.$route.query.searchOnFields) {
              return JSON.parse(this.$route.query.searchOnFields)
            } else {
              return this.searcher.searchOnFieldsToSelect.find(i => i.name) ? this.searcher.searchOnFieldsToSelect.map(i => i.name) : []
            }
          })(),
          dateFrom: this.$route.query.dateTimeFrom || null,
          dateUpTo: this.$route.query.dateTimeUpTo || null,
          aggregates: this.$route.query.aggregates ? JSON.parse(this.$route.query.aggregates) : [],
          orderBy: this.$route.query.orderBy || this.orderBy || null,
          order: this.$route.query.order || this.order || 'desc',
          superPowers: this.$route.query?.superPowers || {},
          polygons: this.$route.query.polygons || null,
          radius: this.$route.query.radius || null,
          lat: this.$route.query.lat || null,
          lng: this.$route.query.lng || null
        }
      }
      this.endpointUri.queryString = ''
      const notQueryIfFalsy = ['offset', 'term', 'dateUpTo', 'dateFrom', 'orderBy', 'aggregates', 'superPowers', 'polygons', 'radius', 'lat', 'lng']
      const query = this.endpointUri.queries
      notQueryIfFalsy.forEach(property => {
        if (query[property] === '{}') {
          delete query[property]
        }
        if (query[property] !== null && typeof query[property] === 'object' && Object.keys(query[property]).length === 0) {
          delete query[property]
        }
        if (Array.isArray(query[property]) && query[property].length === 0) {
          delete query[property]
        } else if (!query[property]) {
          delete query[property]
        }
      })
      Object.keys(query).forEach((key, index) => {
        if (key === 'aggregates' && query[key].length) {
          query[key].forEach((element) => {
            let key = Object.keys(element)
            this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}${key}=${element[key]}`
          })
        } else if (key === 'searchOnFields') {
          query[key].forEach(element => {
            this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}search_on_fields=${element}`
          })
        } else if (key === 'orderBy') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}order_by=${query[key]}`
        } else if (key === 'dateFrom') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}date_from=${query[key]}`
        } else if (key === 'dateUpTo') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}date_up_to=${query[key]}`
        } else if (key === 'polygons') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}polygons=${query[key]}`
        } else if (key === 'radius') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}radius=${query[key]}`
        } else if (key === 'lat') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}lat=${query[key]}`
        } else if (key === 'lng') {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}lng=${query[key]}`
        } else {
          this.endpointUri.queryString += `${this.endpointUri.queryString.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(query[key])}`
        }
      })
    },
    async initialSetting () {
      try {
        this.isContentPageLoading = true
        this.orderBy = null
        const endPoint = await commonsApi.axiosGetWrapper(this.listEndpointUri)
        this.order = endPoint.data.order
        this.orderBy = endPoint.data.initialOrderBy
        this.itemsPerPage = endPoint.data.itemsPerPage
        this.queryApiUrlBuilder()
      } catch (error) {
        console.log('error', error)
      } finally {
        this.isContentPageLoading = false
      }
    },
    async setDataPageAndComponentsProps () {
      try {
        this.isDataProcessing = true
        this.queryApiUrlBuilder()
        const endPointResponse = await commonsApi.axiosGetWrapper(this.endpointUri.path + this.endpointUri.queryString)
        const endPointResponseData = endPointResponse.data
        this.titleList = endPointResponseData.titleList
        this.subTitlesList = endPointResponseData.subTitlesList
        this.table = endPointResponseData.table
        this.tableItemsTotal = endPointResponseData.tableItemsTotal || 0
        this.itemsPerPage = endPointResponseData.itemsPerPage || 10
        this.buttons = endPointResponseData.buttons || {}

        this.pagination = 'pagination' in endPointResponseData ? endPointResponseData.pagination : {}

        this.showTable = 'showTable' in endPointResponseData ? endPointResponseData.showTable : true
        this.showMap = 'showMap' in endPointResponseData ? endPointResponseData.showMap : false
        this.map = endPointResponseData.map

        this.filter.aggregatesToSelect = endPointResponseData.filter?.aggregatesToSelect || {}
        this.filter.totalResult = endPointResponseData.filter?.totalResult || 0
        this.filter.aggregatesSelected = this.$route.query.aggregates ? JSON.parse(this.$route.query.aggregates) : []

        this.searcher.term = endPointResponseData.searcher?.term || this.$route.query.term || null
        this.searcher.searchOnFieldsToSelect = endPointResponseData.searcher?.searchOnFieldsToSelect?.length ? endPointResponseData.searcher?.searchOnFieldsToSelect : []
        if (Array.isArray(endPointResponseData.searcher?.searchOnFieldsToSelect)) {
          this.searcher.searchOnFieldsSelected = this.$route.query.searchOnFields ? JSON.parse(this.$route.query.searchOnFields) : endPointResponseData.searcher?.searchOnFieldsToSelect.map(i => i.name)
        }
        this.searcher.dateFrom = endPointResponseData.searcher?.dateTimeFrom || this.$route.query.dateTimeFrom || null
        this.searcher.dateUpTo = endPointResponseData.searcher?.dateTimeUpTo || this.$route.query.dateTimeUpTo || null
        this.searcher.maxDateUpTo = endPointResponseData.searcher?.maxDateUpTo || 'today'
        this.searcher.showFilterTime = endPointResponseData.searcher?.showFilterTime || false
        this.searcher.lmSuperPowerSelects = endPointResponseData.searcher?.lmSuperPowerSelects || []

        this.orderBy = this.$route.query.orderBy || endPointResponseData.initialOrderBy || null
        this.order = this.$route.query.order || endPointResponseData.order || 'desc'
        this.showLmBreadcrumb = process.env.VUE_APP_SHOWBREADCRUMB === 'yes' ? endPointResponseData.showLmBreadcrumb : false

        this.isTableProcessing = false
      } catch (error) {
        console.log('error', error)
      } finally {
        this.isDataProcessing = false
      }
    }
  },
  watch: {
    async '$route.path' () {
      if (this.showFilters) {
        this.$refs.lmFilter.hide()
      }
      this.$refs.lmSearcher.closeMoreOptions()
      await this.initialSetting()
    },
    async '$route.query' () {
      await this.setDataPageAndComponentsProps()
    }
  },
  async created () {
    await this.initialSetting()
    await this.setDataPageAndComponentsProps()
  }
}
</script>

<style lang="sass" scoped>
.lm-table-while-loading
  height: 300px
.no-search-found
  padding: 5%
  flex-grow: 1
  img
    margin: 0 auto 30px auto
    display: block
    @media(max-width: $md)
      max-width: 50%
  h1,
  h2
    color: $primaryFontThemeColor
    max-width: 700px
    margin: 0 auto
    text-align: center
    @media(max-width: $md)
      min-width: 100px
  h1
    font-size: 24px
    margin-bottom: 10px
    @media(max-width: $md)
      font-size: 18px
  h2
    font-size: 18px
    @media(max-width: $md)
      font-size: 14px
</style>
